"use client";

import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAnyspendCreateOnrampOrder, useAnyspendQuote, useGeoOnrampOptions, useStripeClientSecret } from "../../../../anyspend/react/index.js";
import { USDC_BASE } from "../../../../anyspend/constants/index.js";
import { cn } from "../../../../shared/utils/cn.js";
import { formatUnits } from "../../../../shared/utils/number.js";
import { getStripePromise } from "../../../../shared/utils/payment.utils.js";
import { ShinyButton, TextShimmer, useAccountWallet, useB3Config, useModalStore, useTokenData } from "../../../../global-account/react/index.js";
import { thirdwebB3Chain } from "../../../../shared/constants/chains/b3Chain.js";
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2, Lock, Wallet } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KycGate } from "./KycGate.js";
export function FiatCheckoutPanel({
  recipientAddress,
  destinationTokenAddress,
  destinationTokenChainId,
  totalAmount,
  themeColor,
  onSuccess,
  onOrderCreated,
  onError,
  callbackMetadata,
  classes,
  feeOnTop,
  kycEnabled = false
}) {
  // Stable refs for callback props to avoid re-triggering effects
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const {
    address
  } = useAccountWallet();
  const setB3ModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setB3ModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);
  const {
    data: tokenData
  } = useTokenData(destinationTokenChainId, destinationTokenAddress);
  const {
    theme,
    stripePublishableKey,
    partnerId
  } = useB3Config();
  const handleConnectWallet = useCallback(() => {
    setB3ModalContentType({
      type: "signInWithB3",
      showBackButton: false,
      chain: thirdwebB3Chain,
      partnerId
    });
    setB3ModalOpen(true);
  }, [setB3ModalContentType, setB3ModalOpen, partnerId]);
  // Clean decimal string for API calls (no commas, no subscripts)
  const formattedAmount = useMemo(() => {
    const decimals = tokenData?.decimals || 18;
    return formatUnits(BigInt(totalAmount).toString(), decimals);
  }, [totalAmount, tokenData]);
  // Determine if destination token is a stablecoin (amount ≈ USD)
  const isStablecoin = useMemo(() => {
    return ["0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    // USDC Base
    "0xdAC17F958D2ee523a2206206994597C13D831ec7" // USDT Ethereum
    ].some(addr => addr.toLowerCase() === destinationTokenAddress.toLowerCase());
  }, [destinationTokenAddress]);
  // Get USD equivalent via quote for non-stablecoin tokens
  const {
    anyspendQuote,
    isLoadingAnyspendQuote
  } = useAnyspendQuote({
    type: "swap",
    srcChain: 8453,
    // Base (USDC source)
    dstChain: destinationTokenChainId,
    srcTokenAddress: USDC_BASE.address,
    dstTokenAddress: destinationTokenAddress,
    tradeType: "EXACT_OUTPUT",
    amount: totalAmount
  });
  // USD amount to charge: direct for stablecoins, quote-derived for others.
  // Rounded to 2 decimals so minor quote fluctuations don't retrigger the Stripe support check.
  const usdAmount = useMemo(() => {
    if (isStablecoin) return formattedAmount;
    if (!anyspendQuote?.data?.currencyIn?.amount) return null;
    const raw = formatUnits(anyspendQuote.data.currencyIn.amount, USDC_BASE.decimals);
    return parseFloat(raw).toFixed(2);
  }, [isStablecoin, formattedAmount, anyspendQuote]);
  const {
    geoData,
    stripeOnrampSupport,
    stripeWeb2Support,
    isLoading: isLoadingGeo
  } = useGeoOnrampOptions(usdAmount || "0");
  // KYC state — pre-approved when kycEnabled is false (feature flag off)
  const [kycApproved, setKycApproved] = useState(() => !kycEnabled);
  const handleKycResolved = useCallback(approved => {
    setKycApproved(approved);
  }, []);
  // Order state
  const [orderId, setOrderId] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const orderCreatedRef = useRef(false);
  const {
    createOrder,
    isCreatingOrder
  } = useAnyspendCreateOnrampOrder({
    onSuccess: data => {
      const id = data.data?.id;
      const intentId = data.data?.stripePaymentIntentId;
      if (id && intentId) {
        setOrderId(id);
        setStripePaymentIntentId(intentId);
      } else {
        setOrderError("Failed to initialize payment. Please try again.");
      }
    },
    onError: error => {
      // Backend requires KYC even when kycEnabled=false — reset to show KycGate
      if (error.message?.includes("KYC verification required")) {
        setKycApproved(false);
        orderCreatedRef.current = false;
      } else {
        setOrderError(error.message || "Failed to create payment order.");
      }
      onErrorRef.current?.(error);
    }
  });
  // Reset order error when wallet connects so the user gets a clean state
  useEffect(() => {
    if (address && orderError) {
      setOrderError(null);
      orderCreatedRef.current = false;
    }
  }, [address]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-create onramp order when Stripe Web2 is supported, KYC approved, and all data is ready
  useEffect(() => {
    if (address && !isLoadingGeo && (!isStablecoin ? !isLoadingAnyspendQuote : true) && usdAmount && parseFloat(usdAmount) > 0 && stripeWeb2Support?.isSupport && kycApproved && !orderCreatedRef.current && !orderId && !isCreatingOrder && !orderError && tokenData && recipientAddress) {
      orderCreatedRef.current = true;
      const dstToken = {
        address: destinationTokenAddress,
        chainId: destinationTokenChainId,
        decimals: tokenData.decimals || 18,
        symbol: tokenData.symbol || "",
        name: tokenData.name || "",
        metadata: {
          logoURI: tokenData.logoURI || ""
        }
      };
      createOrder({
        recipientAddress,
        orderType: "swap",
        dstChain: destinationTokenChainId,
        dstToken,
        srcFiatAmount: usdAmount,
        onramp: {
          vendor: "stripe-web2",
          paymentMethod: "",
          country: geoData?.country || "US",
          redirectUrl: window.location.origin
        },
        expectedDstAmount: totalAmount,
        callbackMetadata,
        feeOnTop
      });
    }
  }, [address, isLoadingGeo, isStablecoin, isLoadingAnyspendQuote, usdAmount, stripeWeb2Support, kycApproved, orderId, isCreatingOrder, orderError, tokenData, recipientAddress, destinationTokenAddress, destinationTokenChainId, totalAmount, geoData, callbackMetadata, createOrder, feeOnTop]);
  // Loading geo/stripe support check (and quote for non-stablecoins)
  if (isLoadingGeo || !isStablecoin && isLoadingAnyspendQuote) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-loading flex flex-col items-center gap-3 py-6", classes?.fiatPanel),
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Loading payment form..."
      })]
    });
  }
  const hasStripeWeb2 = stripeWeb2Support && stripeWeb2Support.isSupport;
  const hasStripeRedirect = !!stripeOnrampSupport;
  // Not available in region
  if (!hasStripeWeb2 && !hasStripeRedirect) {
    return _jsx(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-unavailable py-4 text-center", classes?.fiatPanel),
      children: _jsx("p", {
        className: "text-sm text-gray-500 dark:text-gray-400",
        children: "Card payments are not available in your region for this amount."
      })
    });
  }
  // KYC gate — shown before order creation when verification is needed
  if (!kycApproved) {
    return _jsx(KycGate, {
      themeColor: themeColor,
      classes: classes,
      enabled: true,
      onStatusResolved: handleKycResolved
    });
  }
  // No wallet connected — prompt to connect before attempting card payment
  if (!address) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-connect flex flex-col items-center gap-4 py-2", classes?.fiatPanel),
      children: [_jsx(Wallet, {
        className: "h-8 w-8 text-gray-400"
      }), _jsxs("div", {
        className: "text-center",
        children: [_jsx("p", {
          className: "text-sm font-medium text-gray-900 dark:text-gray-100",
          children: "Connect wallet to pay with card"
        }), _jsx("p", {
          className: "mt-1 text-xs text-gray-500 dark:text-gray-400",
          children: "A wallet connection is required to complete card payment."
        })]
      }), _jsx(ShinyButton, {
        accentColor: themeColor || "hsl(var(--as-brand))",
        className: "w-full",
        textClassName: "text-white",
        onClick: handleConnectWallet,
        children: _jsxs("span", {
          className: "flex items-center justify-center gap-2",
          children: [_jsx(Wallet, {
            className: "h-4 w-4"
          }), "Connect Wallet"]
        })
      })]
    });
  }
  // Order creation error - show with retry
  if (orderError) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-error flex flex-col items-center gap-3 py-4", classes?.fiatPanel),
      children: [_jsx("p", {
        className: "text-sm text-red-500",
        children: orderError
      }), _jsx("button", {
        onClick: () => {
          setOrderError(null);
          orderCreatedRef.current = false;
        },
        className: "anyspend-fiat-retry text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400",
        children: "Try again"
      })]
    });
  }
  // Creating order / waiting for PaymentIntent
  if (hasStripeWeb2 && (isCreatingOrder || !stripePaymentIntentId)) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-initializing flex flex-col items-center gap-3 py-6", classes?.fiatPanel),
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Initializing secure payment..."
      })]
    });
  }
  // Stripe Web2 embedded form
  if (hasStripeWeb2 && stripePaymentIntentId && orderId) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0,
        y: 10
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.3,
        ease: "easeOut"
      },
      className: cn("anyspend-fiat-stripe flex flex-col gap-3", classes?.fiatPanel),
      children: [usdAmount && _jsxs("div", {
        className: "anyspend-fiat-summary rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700",
        children: [_jsxs("div", {
          className: "flex items-center justify-between text-sm",
          children: [_jsx("span", {
            className: "text-gray-500 dark:text-gray-400",
            children: "Total"
          }), _jsxs("span", {
            className: "font-medium text-gray-900 dark:text-white",
            children: ["$", parseFloat(usdAmount).toFixed(2)]
          })]
        }), !isStablecoin && tokenData && _jsxs("div", {
          className: "mt-1 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500",
          children: [_jsxs("span", {
            children: [formattedAmount, " ", tokenData.symbol]
          }), _jsx("span", {
            children: "incl. fees"
          })]
        })]
      }), _jsx(StripeCheckout, {
        stripePaymentIntentId: stripePaymentIntentId,
        stripePublishableKey: stripePublishableKey,
        theme: theme,
        themeColor: themeColor,
        orderId: orderId,
        onSuccess: onSuccess,
        onOrderCreated: onOrderCreated,
        onError: onError,
        classes: classes
      })]
    });
  }
  // TODO: Dead code — Stripe onramp is disabled at backend (isStripeOnrampSupported hardcoded false).
  // Remove this block or implement onClick if Stripe onramp is re-enabled.
  return _jsxs("div", {
    className: cn("anyspend-fiat-redirect flex flex-col gap-3 py-2", classes?.fiatPanel),
    children: [_jsx("p", {
      className: "text-sm text-gray-600 dark:text-gray-400",
      children: "You'll be redirected to Stripe to complete your payment securely."
    }), _jsx(ShinyButton, {
      accentColor: themeColor || "hsl(var(--as-brand))",
      className: "anyspend-fiat-redirect-btn w-full",
      textClassName: "text-white",
      children: _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [_jsx(Lock, {
          className: "h-3.5 w-3.5"
        }), "Pay with Card"]
      })
    }), _jsxs("p", {
      className: "anyspend-fiat-secured flex items-center justify-center gap-1 text-xs text-gray-400",
      children: [_jsx(Lock, {
        className: "h-3 w-3"
      }), "Secured by Stripe"]
    })]
  });
}
function StripeCheckout(t0) {
  const $ = _c(13);
  const {
    stripePaymentIntentId,
    stripePublishableKey,
    theme,
    themeColor,
    orderId,
    onSuccess,
    onOrderCreated,
    onError,
    classes
  } = t0;
  const {
    clientSecret,
    isLoadingStripeClientSecret,
    stripeClientSecretError
  } = useStripeClientSecret(stripePaymentIntentId);
  if (isLoadingStripeClientSecret) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = _jsxs(motion.div, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        className: "anyspend-stripe-loading flex flex-col items-center gap-3 py-6",
        children: [_jsx(Loader2, {
          className: "h-5 w-5 animate-spin text-gray-400"
        }), _jsx(TextShimmer, {
          duration: 1.5,
          className: "text-sm",
          children: "Loading payment form..."
        })]
      });
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (stripeClientSecretError || !clientSecret) {
    let t1;
    if ($[1] !== stripeClientSecretError?.message) {
      t1 = _jsx(motion.div, {
        initial: {
          opacity: 0,
          y: 8
        },
        animate: {
          opacity: 1,
          y: 0
        },
        transition: {
          duration: 0.25,
          ease: "easeOut"
        },
        className: "anyspend-stripe-error py-4 text-center",
        children: _jsx("p", {
          className: "text-sm text-red-500",
          children: stripeClientSecretError?.message || "Failed to load payment form. Please try again."
        })
      });
      $[1] = stripeClientSecretError?.message;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== classes || $[4] !== clientSecret || $[5] !== onError || $[6] !== onOrderCreated || $[7] !== onSuccess || $[8] !== orderId || $[9] !== stripePublishableKey || $[10] !== theme || $[11] !== themeColor) {
    t1 = _jsx(Elements, {
      stripe: getStripePromise(stripePublishableKey),
      options: {
        clientSecret,
        appearance: {
          theme: theme === "light" ? "stripe" : "night",
          variables: {
            borderRadius: "8px"
          }
        }
      },
      children: _jsx(StripeCheckoutForm, {
        themeColor,
        orderId,
        onSuccess,
        onOrderCreated,
        onError,
        classes
      })
    });
    $[3] = classes;
    $[4] = clientSecret;
    $[5] = onError;
    $[6] = onOrderCreated;
    $[7] = onSuccess;
    $[8] = orderId;
    $[9] = stripePublishableKey;
    $[10] = theme;
    $[11] = themeColor;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function StripeCheckoutForm({
  themeColor,
  orderId,
  onSuccess,
  onOrderCreated,
  onError,
  classes
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [showAddressElement, setShowAddressElement] = useState(false);
  useEffect(() => {
    if (stripe && elements) {
      setStripeReady(true);
    }
  }, [stripe, elements]);
  const handlePaymentElementChange = useCallback(event => {
    setShowAddressElement(event.value?.type === "card");
  }, []);
  const handleSubmit = async e => {
    e.preventDefault();
    if (!stripe || !elements) {
      setMessage("Payment system is not ready. Please wait.");
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required"
      });
      if (result.error) {
        console.error("@@checkout-stripe:error:", JSON.stringify(result.error, null, 2));
        setMessage(result.error.message || "Payment failed. Please try again.");
        return;
      }
      console.log("@@checkout-stripe:success:", JSON.stringify({
        orderId
      }, null, 2));
      // Payment succeeded — notify parent to show order lifecycle tracking
      onOrderCreated?.(orderId);
      // Also fire legacy callback for backward compatibility
      onSuccess?.({
        orderId,
        txHash: undefined
      });
    } catch (error) {
      const errorMessage = error?.message || "Payment failed. Please try again.";
      setMessage(errorMessage);
      const errorObj = error instanceof Error ? error : new Error(errorMessage);
      onError?.(errorObj);
    } finally {
      setLoading(false);
    }
  };
  const stripeElementOptions = {
    layout: "tabs",
    fields: {
      billingDetails: "auto"
    },
    wallets: {
      applePay: "auto",
      googlePay: "auto"
    }
  };
  if (!stripeReady) {
    return _jsxs(motion.div, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        ease: "easeOut"
      },
      className: "anyspend-stripe-loading flex flex-col items-center gap-3 py-6",
      children: [_jsx(Loader2, {
        className: "h-5 w-5 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Loading payment form..."
      })]
    });
  }
  return _jsxs(motion.form, {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: "easeOut"
    },
    onSubmit: handleSubmit,
    className: "anyspend-stripe-form flex flex-col gap-4",
    children: [_jsx("div", {
      className: "anyspend-stripe-payment-element",
      children: _jsx(PaymentElement, {
        onChange: handlePaymentElementChange,
        options: stripeElementOptions
      })
    }), _jsx(AnimatePresence, {
      initial: false,
      children: showAddressElement && _jsx(motion.div, {
        initial: {
          height: 0,
          opacity: 0
        },
        animate: {
          height: "auto",
          opacity: 1
        },
        exit: {
          height: 0,
          opacity: 0
        },
        transition: {
          duration: 0.25,
          ease: "easeOut"
        },
        style: {
          overflow: "hidden"
        },
        className: "anyspend-stripe-address-element",
        children: _jsx(AddressElement, {
          options: {
            mode: "billing",
            fields: {
              phone: "always"
            },
            display: {
              name: "split"
            },
            validation: {
              phone: {
                required: "always"
              }
            }
          }
        })
      }, "address-element")
    }), _jsx(AnimatePresence, {
      initial: false,
      children: message && _jsx(motion.div, {
        initial: {
          opacity: 0,
          height: 0
        },
        animate: {
          opacity: 1,
          height: "auto"
        },
        exit: {
          opacity: 0,
          height: 0
        },
        transition: {
          duration: 0.2,
          ease: "easeOut"
        },
        style: {
          overflow: "hidden"
        },
        className: "anyspend-stripe-error rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
        children: _jsx("p", {
          className: "text-sm text-red-600 dark:text-red-400",
          children: message
        })
      }, "stripe-error")
    }), _jsx(ShinyButton, {
      type: "submit",
      accentColor: themeColor || "hsl(var(--as-brand))",
      disabled: !stripe || !elements || loading,
      className: cn("anyspend-stripe-submit w-full", classes?.payButton),
      textClassName: "text-white",
      children: loading ? _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [_jsx(Loader2, {
          className: "h-4 w-4 animate-spin"
        }), "Processing..."]
      }) : _jsxs("span", {
        className: "flex items-center justify-center gap-2",
        children: [_jsx(Lock, {
          className: "h-3.5 w-3.5"
        }), "Complete Payment"]
      })
    }), _jsxs("p", {
      className: "anyspend-fiat-secured flex items-center justify-center gap-1 text-xs text-gray-400",
      children: [_jsx(Lock, {
        className: "h-3 w-3"
      }), "Secured by Stripe"]
    })]
  });
}